"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UniversalHeaderAccumulator = exports.toAuditorMobileRuleId = void 0;
exports.mobileSdkComponentFor = mobileSdkComponentFor;
exports.iterateUniversalExportIssues = iterateUniversalExportIssues;
exports.buildHeader = buildHeader;
exports.buildHeaderFromAggregate = buildHeaderFromAggregate;
exports.buildUniversalExport = buildUniversalExport;
const accessibility_standards_1 = __importDefault(require("@deque/accessibility-standards"));
const uuid_1 = require("uuid");
const mobilePlatform_1 = require("../mobilePlatform");
const auditorRuleIds_1 = require("./auditorRuleIds");
Object.defineProperty(exports, "toAuditorMobileRuleId", { enumerable: true, get: function () { return auditorRuleIds_1.toAuditorMobileRuleId; } });
/**
 * Fixed namespace UUID for `issueId` derivation. NEVER change this value —
 * doing so invalidates issueId stability across every previously-emitted
 * artifact.
 */
const ISSUE_ID_NAMESPACE = '8b6a3f1c-2f7d-5e9a-9b4c-3d4f5a6b7c8d';
const UNKNOWN = 'unknown';
/**
 * Shared by the stored artifact and the page-built export so they agree. A
 * null engine means no platform resolved, which ingestion only admits for
 * legacy Android scans — see the `mobileEngine` comment below.
 */
function mobileSdkComponentFor(engine) {
    return engine === 'axe-ios' ? 'sdk-ios' : 'sdk-android';
}
function isMobilePageState(pageState) {
    return (0, mobilePlatform_1.isMobileMetadata)(pageState?.metadata);
}
/**
 * Mobile has no HTML source; the converter packs the scan's analyzed values
 * (className, detected text, color contrasts, …) into the node's `html` slot
 * as a JSON string. Parse it back to an object for `analyzedValues`, or
 * return undefined when missing/malformed so the field is omitted.
 */
function parseAnalyzedValues(html) {
    if (!html)
        return undefined;
    try {
        const parsed = JSON.parse(html);
        return typeof parsed === 'object' && parsed !== null ? parsed : undefined;
    }
    catch {
        return undefined;
    }
}
function exportRuleId(result, pageState) {
    return isMobilePageState(pageState)
        ? (0, auditorRuleIds_1.toAuditorMobileRuleId)(result.id)
        : result.id;
}
/**
 * Internal: map a single deduped node → `UniversalIssue`.
 *
 * Object-literal key order here defines the on-wire key order under
 * `JSON.stringify` (V8 preserves insertion order). Do not reorder casually —
 * it changes the byte-exact output and breaks the deterministic-byte
 * assertions in the streaming write tests.
 *
 * Walnut's `UniversalIssue` is looser than the CEF spec: `issueId` allows
 * null, `axeVersion` is optional, `impact` allows null, and there are
 * extension/mobile/ignore fields we never populate. We populate the
 * CEF-required fields with concrete values regardless — the wire output
 * stays spec-compliant; the type just won't catch a missed field at compile
 * time.
 */
function mapIssue(session, result, unique, pageState, needsReview) {
    // `fingerprints` are rule + url + selector with no status in the mix, so the
    // same node can be a violation on one page state and Needs Review on another
    // and reach both tracks — which dedupe against separate key sets — with an
    // identical fingerprint. Discriminating the Needs Review name keeps the two
    // entries from colliding on `issueId`. Violations keep their existing
    // derivation byte-for-byte.
    const issueIdName = unique.fingerprints
        ? needsReview
            ? `${unique.fingerprints.toString()}|INCOMPLETE`
            : unique.fingerprints.toString()
        : (0, uuid_1.v4)();
    const issue = {
        issueId: (0, uuid_1.v5)(issueIdName, ISSUE_ID_NAMESPACE),
        ruleId: exportRuleId(result, pageState),
        description: result.description,
        help: result.help,
        helpUrl: result.helpUrl,
        impact: result.impact,
        needsReview,
        // Parity with devtools-mobile-service's `AxeUniversalFormatRuleResult.status`.
        // Needs Review only: stamping 'FAIL' on violations would change both the
        // flag-off bytes and the flag-on-but-no-needs-review bytes.
        ...(needsReview && { status: 'INCOMPLETE' }),
        isManual: false,
        isExperimental: result.tags.includes('experimental'),
        isAdvanced: result.tags.includes('advanced'),
        summary: unique.failureSummary,
        tags: result.tags,
        createdAt: unique.created_at.toISOString(),
        fingerprints: unique.fingerprints,
        shareURL: null,
        integrationUrl: null,
        axeVersion: session.axe_core_version ?? session.data_source_version ?? UNKNOWN
    };
    if (unique !== undefined) {
        // The typing from Walnut's `UniversalIssue` is wrong
        issue.selector = unique.target;
    }
    if (isMobilePageState(pageState)) {
        // Mobile issues have no HTML source — the spec wants `source: null` with
        // the scan's analyzed values (packed into `html` as a JSON string by
        // `convertMobileResult`) emitted under `analyzedValues` instead.
        // Walnut's `source?: string` typing doesn't admit the spec's null.
        issue.source = null;
        const analyzedValues = parseAnalyzedValues(unique.html);
        if (analyzedValues !== undefined) {
            issue.analyzedValues = analyzedValues;
        }
    }
    else if (unique.html) {
        issue.source = unique.html;
    }
    if (pageState.url) {
        issue.testUrl = pageState.url;
    }
    if (pageState.page_state) {
        issue.testPageTitle = pageState.page_state;
    }
    return issue;
}
/**
 * Public: per-issue generator. The streaming serializer iterates this so
 * memory carries at most one mapped issue at a time.
 *
 * Input order is trusted — the deduped artifact in S3 is already canonically
 * ordered by the dedupe step, so we just walk it.
 */
function* iterateUniversalExportIssues(session, pageStateResults, needsReviewPageStateResults = []) {
    for (const pageState of pageStateResults) {
        for (const result of pageState.result) {
            for (const unique of result.uniques) {
                yield mapIssue(session, result, unique, pageState, false);
            }
        }
    }
    // Appended, never interleaved: violation entries and their order must stay
    // byte-identical to a violations-only export.
    for (const pageState of needsReviewPageStateResults) {
        for (const result of pageState.result) {
            for (const unique of result.uniques) {
                yield mapIssue(session, result, unique, pageState, true);
            }
        }
    }
}
/**
 * `buildHeader`'s aggregation one page state at a time. It keeps only the
 * first page state's metadata, which is all the header reads from it, so no
 * page state is retained.
 */
class UniversalHeaderAccumulator {
    #issueSummary = {
        critical: 0,
        serious: 0,
        moderate: 0,
        minor: 0,
        bestPractices: 0,
        needsReview: 0
    };
    #ruleCounts = new Map();
    #needsReviewCounts = new Map();
    #firstPageState = null;
    #earliest = null;
    #latest = null;
    addPageState(pageState) {
        const ts = pageState.created_at.getTime();
        if (this.#earliest === null || ts < this.#earliest) {
            this.#earliest = ts;
            this.#firstPageState = { metadata: pageState.metadata };
        }
        if (this.#latest === null || ts > this.#latest) {
            this.#latest = ts;
        }
        for (const result of pageState.result) {
            const nodeCount = result.uniques.length;
            this.#issueSummary[result.impact] += nodeCount;
            const ruleId = exportRuleId(result, pageState);
            this.#ruleCounts.set(ruleId, (this.#ruleCounts.get(ruleId) ?? 0) + nodeCount);
        }
    }
    /**
     * Needs Review deliberately contributes to NO impact bucket and no
     * `failedRules` entry, so those counts keep summing to the total issue count.
     * This diverges from the extension's `getIssueSummary`, which folds
     * needs-review into the impact buckets. Per-rule counts are instance counts
     * over the already-deduplicated input — the extension's
     * `getIssueRules(issues, true)` method — so the summary reconciles with the
     * entries emitted into `allIssues`.
     */
    addNeedsReviewPageState(pageState) {
        for (const result of pageState.result) {
            const nodeCount = result.uniques.length;
            this.#issueSummary.needsReview += nodeCount;
            const ruleId = exportRuleId(result, pageState);
            this.#needsReviewCounts.set(ruleId, (this.#needsReviewCounts.get(ruleId) ?? 0) + nodeCount);
        }
    }
    aggregate() {
        const toRules = (counts) => [...counts.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([name, count]) => ({ name, count, mode: 'automated' }));
        return {
            issueSummary: { ...this.#issueSummary },
            failedRules: toRules(this.#ruleCounts),
            needsReviewRules: toRules(this.#needsReviewCounts),
            firstPageState: this.#firstPageState,
            testingStartDate: this.#earliest === null ? null : new Date(this.#earliest).toISOString(),
            testingEndDate: this.#latest === null ? null : new Date(this.#latest).toISOString()
        };
    }
}
exports.UniversalHeaderAccumulator = UniversalHeaderAccumulator;
function aggregate(pageStateResults, needsReviewPageStateResults) {
    const acc = new UniversalHeaderAccumulator();
    pageStateResults.forEach(p => acc.addPageState(p));
    needsReviewPageStateResults.forEach(p => acc.addNeedsReviewPageState(p));
    return acc.aggregate();
}
/**
 * Public: build the universal-export document header WITHOUT `allIssues`. The
 * streaming serializer writes this as the document prefix, then streams
 * individual issues from `iterateUniversalExportIssues` into the open
 * `allIssues` array.
 *
 * Key-order discipline: the returned object's literal field order IS the
 * on-wire order under `JSON.stringify`. Keep insertion order stable.
 */
function buildHeader(session, pageStateResults, needsReviewPageStateResults = []) {
    return buildHeaderFromAggregate(session, aggregate(pageStateResults, needsReviewPageStateResults));
}
/**
 * Public: `buildHeader` from already-aggregated counts, for callers that
 * accumulate page states as they stream rather than holding them all.
 */
function buildHeaderFromAggregate(session, agg) {
    // `new Date(...)` coercion: the session may have crossed a JSON boundary
    // (Lambda event payload), flattening `Date` fields to ISO strings — calling
    // `.toISOString()` on those directly throws. `new Date()` accepts both
    // shapes (cloning a `Date`, parsing a string).
    const startDate = agg.testingStartDate ?? new Date(session.created_at).toISOString();
    const endDate = agg.testingEndDate ??
        new Date(session.updated_at ?? session.created_at).toISOString();
    // The chronologically-first page state is representative for the session:
    // a session is all-mobile or all-web (results arrive through a single
    // ingestion route per session), so one page state classifies the document.
    //
    // Platform detection mirrors ingestion's `detectMobilePlatform` fallback
    // chain (`platform.name` = raw OS string, then `device.name` — e.g.
    // "iPhone 15" → iOS).
    const firstMetadata = agg.firstPageState?.metadata;
    const mobilePlatform = (0, mobilePlatform_1.normaliseMobileOS)(firstMetadata?.platform?.name) ??
        (0, mobilePlatform_1.normaliseMobileOS)(firstMetadata?.device?.name);
    const isMobile = isMobilePageState(agg.firstPageState);
    const axeVersion = session.axe_core_version ?? session.data_source_version ?? UNKNOWN;
    const sessionSettings = session.settings;
    const rawStandard = sessionSettings?.accessibilityStandard?.value;
    // Mobile is not yet in global configuration, so its standard is fixed.
    // Web: map the raw enum to its display name ('wcag22aa' → 'WCAG 2.2 AA'),
    // falling back to the raw value for unmapped standards.
    const standard = isMobile
        ? 'WCAG 2.2 AA'
        : rawStandard !== undefined
            ? (accessibility_standards_1.default[rawStandard]?.name ?? rawStandard)
            : UNKNOWN;
    const bestPracticesEnabled = sessionSettings && sessionSettings.bestPractices?.value === 'enabled';
    const experimentalEnabled = sessionSettings && sessionSettings.experimentalRules?.value === 'enabled';
    // A mobile page state with no resolvable platform is Android by ingestion's
    // invariant: `detectMobilePlatform` rejects results it can't place on a
    // platform, with one exception — legacy Android scans missing axeDevice
    // os/name, admitted via a className heuristic.
    const engine = isMobile
        ? mobilePlatform === null || mobilePlatform === mobilePlatform_1.MobilePlatform.ANDROID
            ? 'axe-android'
            : 'axe-ios' // iOS and iPadOS both ship axe-ios
        : 'axe-core';
    const source = {
        productName: isMobile ? 'axe-devtools-mobile' : 'axe-devtools-html',
        // `session.data_source` is the raw ingestion label, not an SDK component
        // name, so mirror the platform already resolved into `engine`.
        productComponentName: engine === 'axe-core'
            ? (session.data_source ?? UNKNOWN)
            : mobileSdkComponentFor(engine),
        productVersion: session.data_source_version ?? UNKNOWN
    };
    const testDetails = {
        testId: session.id,
        testName: session.name ?? '',
        // The email of the user who created the session, captured at session-init
        // (results-team#369). Null for sessions created before that shipped, or
        // when the creator has no resolvable email (e.g. pipeline / API-key-only).
        createdBy: session.created_by_user_email ?? null,
        startDate,
        endDate,
        engine,
        axeVersion,
        standard,
        bestPracticesEnabled: bestPracticesEnabled === true,
        experimentalEnabled: experimentalEnabled === true,
        integrationTestId: null
    };
    // Top level object "header". `standard` always resolves (falling back to
    // 'unknown'), so it's emitted unconditionally; the settings-derived flag
    // fields are inserted conditionally so they're omitted (not emitted as
    // `undefined`) when session.settings is null.
    const header = {
        axeVersion,
        standard,
        testingStartDate: startDate,
        testingEndDate: endDate,
        ...(bestPracticesEnabled !== null && { bestPracticesEnabled }),
        ...(experimentalEnabled !== null && { experimentalEnabled }),
        issueSummary: agg.issueSummary,
        failedRules: agg.failedRules,
        needsReview: agg.needsReviewRules,
        source,
        testDetails
    };
    return header;
}
/**
 * Public: one-shot universal-export builder. Used by tests and any
 * non-streaming consumer; the streaming write path uses `buildHeader`
 * + `iterateUniversalExportIssues` instead so `allIssues` never materializes
 * in memory in full.
 */
function buildUniversalExport(session, pageStateResults, needsReviewPageStateResults = []) {
    return {
        ...buildHeader(session, pageStateResults, needsReviewPageStateResults),
        allIssues: [
            ...iterateUniversalExportIssues(session, pageStateResults, needsReviewPageStateResults)
        ]
    };
}
//# sourceMappingURL=index.js.map